import { BaseBuilder, BaseBuilderOptions, IBuilder } from './BaseBuilder.ts';
import { ProductCriteriaOptions } from '@services/MarppleShop/features/ProductList/type/product-list.request.ts';
import { CommonPageType, CommonRankType, CommonFilters } from './shared/common-types';
import { PageManager, RankManager, FilterManager, SortManager } from './managers';

export type GoodsPage = CommonPageType | 'LATEST_SOLD';
export type GoodsRankType = CommonRankType;

interface GoodsFilters extends CommonFilters {
  min_price?: number;
  max_price?: number;
  style_filter_ids?: number[];
  creator_filter_ids?: number[];
}

interface GoodsBuilderOptions extends BaseBuilderOptions {
  page?: GoodsPage;
  baseUrl?: string;
  filters?: GoodsFilters;
  sort?: ProductCriteriaOptions['sort'];
  rankType?: GoodsRankType;
}

/**
 * 일반 상품 전용 URL 빌더
 */
export class GoodsBuilder implements IBuilder {
  private baseBuilder: BaseBuilder;
  private customBaseUrl?: string;

  private pageManager: PageManager<GoodsPage>;
  private rankManager: RankManager<GoodsRankType>;
  private filterManager: FilterManager<GoodsFilters>;
  private sortManager: SortManager;

  // string 타입 기반 URL 매핑
  private static readonly PAGE_URL_MAP: Record<GoodsPage, string> = {
    RANKING: '/:lang/@/goods/ranking',
    PRODUCTS: '/:lang/@/goods/products',
    LATEST_SOLD: '/:lang/@/realtime', // 특수한 경우
  };

  constructor(initialOptions: GoodsBuilderOptions) {
    this.baseBuilder = new BaseBuilder(initialOptions);
    this.customBaseUrl = initialOptions.baseUrl;

    this.pageManager = new PageManager(initialOptions.page, 'RANKING', GoodsBuilder.PAGE_URL_MAP);

    this.rankManager = new RankManager(initialOptions.rankType, 'REALTIME');

    this.filterManager = new FilterManager(initialOptions.filters, {});

    this.sortManager = new SortManager(initialOptions.sort, 'popular');
  }

  private getBaseUrl(): string {
    // 커스텀 URL이 있으면 우선 사용
    if (this.customBaseUrl) {
      return this.customBaseUrl;
    } else {
      const lang = this.baseBuilder.getLangOrThrow();
      return this.pageManager.getPageUrl(lang);
    }
  }

  setPage(page: GoodsPage): this {
    this.pageManager.setPage(page);
    return this;
  }

  setBaseUrl(baseUrl: string): this {
    this.customBaseUrl = baseUrl;
    return this;
  }

  setFilters(filters: Omit<GoodsFilters, 'cate_root_id'>): this {
    this.filterManager.setFilters(filters);
    return this;
  }

  setSort(sort: ProductCriteriaOptions['sort']): this {
    this.sortManager.setSort(sort);
    return this;
  }

  setRankType(rankType: GoodsRankType): this {
    this.rankManager.setRankType(rankType);
    return this;
  }

  build(): string {
    const baseUrl = this.getBaseUrl();

    if (this.pageManager.isRanking()) {
      return this.rankManager.buildRankingUrl(baseUrl);
    }

    if (this.pageManager.isProducts()) {
      const goodsSpecificParams = this.buildSpecificParams();
      return this.filterManager.buildProductsUrl(baseUrl, this.sortManager.getSort(), goodsSpecificParams);
    }

    return baseUrl;
  }

  clone(): GoodsBuilder {
    return new GoodsBuilder({
      lang: this.baseBuilder.getLangOrThrow(),
      cate_root_id: this.baseBuilder.getCateRoot().id,
      page: this.pageManager.getPage(),
      baseUrl: this.customBaseUrl,
      filters: this.filterManager.getFilters(),
      sort: this.sortManager.getSort(),
      rankType: this.rankManager.getRankType(),
    });
  }

  private buildSpecificParams(): Record<string, string> {
    const goodsSpecificParams: Record<string, string> = {};
    const filters = this.filterManager.getFilters();

    if (filters.min_price !== undefined) {
      goodsSpecificParams.min_price = String(filters.min_price);
    }
    if (filters.max_price !== undefined) {
      goodsSpecificParams.max_price = String(filters.max_price);
    }
    if (filters.style_filter_ids?.length) {
      goodsSpecificParams.style_filter_ids = filters.style_filter_ids.join(',');
    }
    if (filters.creator_filter_ids?.length) {
      goodsSpecificParams.creator_filter_ids = filters.creator_filter_ids.join(',');
    }

    return goodsSpecificParams;
  }
}
